import { useMemo } from "react";
import { ICodename, IModuleResourceResponse, RoleResourcePermission } from "../commons/interfaces";
import { Permission, RoleTableMode } from "../constants/enums";
import useStrings from "./useStrings";
import Helpers from "../commons/helpers";

// Read = 32768, // Xem
// ListOrExport = 16384, // Xem danh sách, xuất file (mở rộng của quyền đọc)
// Create = 8192, // Tạo
// BulkCreateOrImport = 4096, // Tạo nhiều hoặc import file (mở rộng của quyền tạo)
// Update = 2048, // Cập nhật
// BulkUpdate = 1024, // Cập nhật nhiều (mở rộng của cập nhật)
// Delete = 512, // Xóa
// BulkDelete =  256, // Xóa nhiều (mở rộng của xóa)
// None = 0

export const usePermissionTable = <T extends IModuleResourceResponse | RoleResourcePermission>({
    data,
    checkByLevel,
    tableMode = "normal",
    onChangePermission,
}: {
    tableMode?: RoleTableMode;
    checkByLevel?: boolean;
    data: T[];
    onChangePermission: (values: T[]) => void;
}) => {
    const Strings = useStrings();

    const isRoleResourcePermission = (item: RoleResourcePermission | IModuleResourceResponse): item is RoleResourcePermission => "policyId" in item;
    const isModuleResource = (item: RoleResourcePermission | IModuleResourceResponse): item is IModuleResourceResponse =>
        "moduleCode" in item || !("policyId" in item);

    const permissionList = useMemo<ICodename[]>(
        () => [
            {
                name: tableMode === "normal" ? Strings.Role.READ : Strings.Role.VIEW_DETAIL,
                code: Permission.Read,
            },
            {
                name: Strings.Role.READ_LIST,
                code: Permission.ListOrExport,
            },

            {
                name: Strings.Role.CREATE,
                code: Permission.Create,
            },
            {
                name: Strings.Role.BULK_CREATE,
                code: Permission.BulkCreateOrImport,
            },

            {
                name: Strings.Role.UPDATE,
                code: Permission.Update,
            },
            {
                name: Strings.Role.BULK_UPDATE,
                code: Permission.BulkUpdate,
            },
            {
                name: Strings.Role.DELETE,
                code: Permission.Delete,
            },
            {
                name: Strings.Role.BULK_DELETE,
                code: Permission.BulkDelete,
            },
        ],
        [Strings.getLanguage(), tableMode]
    );

    const permissionValues = useMemo<Permission[]>(
        () =>
            permissionList.reduce((acc, cur) => {
                acc.push(cur.code);
                return acc;
            }, []),
        [permissionList]
    );

    const advancePermissions = useMemo<Permission[]>(
        () => [Permission.ListOrExport, Permission.BulkCreateOrImport, Permission.BulkUpdate, Permission.BulkDelete],
        []
    );

    const fullPermission = useMemo<number>(() => permissionValues.reduce((acc, cur) => (acc = acc | cur), 0), [permissionValues]);

    const checkAll = useMemo(() => {
        return (
            (data || []).reduce((acc, cur) => {
                if (isRoleResourcePermission(cur) && !Helpers.isNullOrEmpty(cur.enablePermission))
                    return cur.permission === cur.enablePermission ? (acc = acc + 1) : acc;
                if (isRoleResourcePermission(cur) && Helpers.isNullOrEmpty(cur.enablePermission))
                    return cur.permission === fullPermission ? (acc = acc + 1) : acc;

                if (isModuleResource(cur)) return cur.editMode === fullPermission ? (acc = acc + 1) : acc;
                return acc;
            }, 0) === data.length
        );
    }, [data, fullPermission]);

    const getAdvancePermissionInEnablePermission = (permissionValues: number, permission: Permission) =>
        (permissionValues & permission) === permission ? permission : 0;

    const getPermission = (permission: Permission, enablePermission?: number) =>
        enablePermission ? getAdvancePermissionInEnablePermission(enablePermission, permission) : permission;

    const getBulkOfPermission = (permission: Permission, enablePermission?: number) => {
        if (tableMode !== "normal") return 0;

        if (permission === Permission.Read) return getPermission(Permission.ListOrExport, enablePermission);
        if (permission === Permission.Create) return getPermission(Permission.BulkCreateOrImport, enablePermission);

        if (permission === Permission.Update) return getPermission(Permission.BulkUpdate, enablePermission);
        if (permission === Permission.Delete) return getPermission(Permission.BulkDelete, enablePermission);
        return 0;
    };

    const onChangeValue = (permission: Permission, index: number) => (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        event.stopPropagation();

        const temp = [...(data || [])];
        const item = temp[index];

        if (!checkByLevel) {
            if (isRoleResourcePermission(item) && checked)
                item.permission = item?.permission | permission | getBulkOfPermission(permission, item?.enablePermission);
            if (isRoleResourcePermission(item) && !checked)
                item.permission = item?.permission & ~permission & ~getBulkOfPermission(permission, item?.enablePermission);

            if (isModuleResource(item) && checked) item.editMode = item?.editMode | permission | getBulkOfPermission(permission);
            if (isModuleResource(item) && !checked) item.editMode = item?.editMode & ~permission & ~getBulkOfPermission(permission);
        }

        if (checkByLevel) {
            const indexOfPermisson = permissionValues.findIndex((el) => el === permission);

            if (isRoleResourcePermission(item) && checked) {
                for (let i = 0; i <= indexOfPermisson; i++) item.permission = item?.permission | permissionValues[i];
                item.permission = item?.permission | getBulkOfPermission(permission, item?.enablePermission);
            }
            if (isRoleResourcePermission(item) && !checked && advancePermissions.includes(permission))
                item.permission = item?.permission & ~permission;
            if (isRoleResourcePermission(item) && !checked && !advancePermissions.includes(permission))
                for (let i = permissionValues.length; i > indexOfPermisson - 1; i--) item.permission = item?.permission & ~permissionValues[i];

            if (isModuleResource(item) && checked) {
                for (let i = 0; i <= indexOfPermisson; i++) item.editMode = item?.editMode | permissionValues[i];
                item.editMode = item?.editMode | getBulkOfPermission(permission);
            }
            if (isModuleResource(item) && !checked && advancePermissions.includes(permission)) item.editMode = item?.editMode & ~permission;
            if (isModuleResource(item) && !checked && !advancePermissions.includes(permission))
                for (let i = permissionValues.length; i > indexOfPermisson - 1; i--) item.editMode = item?.editMode & ~permissionValues[i];
        }

        onChangePermission(temp);
        return temp;
    };

    const onCheckAll = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        event.stopPropagation();
        const temp = [...(data || [])];
        for (const item of temp) {
            if (isRoleResourcePermission(item) && checked) item.permission = fullPermission;
            if (isRoleResourcePermission(item) && checked && !Helpers.isNullOrEmpty(item.enablePermission)) item.permission = item.enablePermission;
            if (isRoleResourcePermission(item) && !checked) item.permission = 0;

            if (isModuleResource(item) && checked) item.editMode = fullPermission;
            if (isModuleResource(item) && !checked) item.editMode = 0;
        }

        onChangePermission(temp as T[]);
    };

    return {
        permissionList,
        permissionValues,
        advancePermissions,
        fullPermission,
        checkAll,
        onChangeValue,
        onCheckAll,
    };
};

export default usePermissionTable;
